"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { Sparkles } from "lucide-react";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type PlaygroundUpgradeModalProps = {
  open: boolean;
  onClose: () => void;
  locale: string;
};

export function PlaygroundUpgradeModal({ open, onClose, locale }: PlaygroundUpgradeModalProps) {
  const t = useTranslations("playground");
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /**
   * Focus in, trapped, and back out again.
   *
   * The native `<dialog>` the other playground modals use gives this for free;
   * this one is a portalled div (it has to sit above a Radix menu), so it does
   * the same three things by hand: move focus inside on open, keep Tab from
   * walking behind the overlay, and return focus to whatever opened it.
   */
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      (panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel).focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      restoreRef.current?.focus?.();
    };
  }, [open]);

  const onPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !mounted) return null;

  const pricingHref = `/${locale}/quiz-exams/pricing`;

  const features = [
    t("upgradeFeatureSnippets"),
    t("upgradeFeatureFolders"),
    t("upgradeFeatureUrls"),
    t("upgradeFeatureAi"),
    t("upgradeFeatureRuns"),
  ];

  /**
   * Portal to `document.body` so `fixed` is viewport-relative even when it is
   * opened from inside a transformed or filtered ancestor. The scrim is opaque
   * rather than blurred: `backdrop-filter` is forbidden by `AGENTS.md` rule 9.
   */
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="playground-upgrade-title"
      onKeyDown={onPanelKeyDown}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#141416] p-6 shadow-2xl outline-none"
      >
        <div className="mb-3 flex items-center gap-2 text-white">
          <Sparkles className="h-5 w-5 shrink-0 text-[#FFAB8F]" aria-hidden />
          <h2 id="playground-upgrade-title" className="text-lg font-semibold tracking-tight">
            {t("upgradeModalTitle")}
          </h2>
        </div>
        <p className="text-sm leading-relaxed text-white/70">{t("upgradeModalIntro")}</p>
        <ul className="mt-4 list-inside list-disc space-y-1.5 text-sm text-white/85">
          {features.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="mt-4 text-xs leading-relaxed text-white/50">{t("upgradeModalSecondary")}</p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141416]"
          >
            {t("upgradeModalClose")}
          </button>
          <Link
            href={pricingHref}
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#E94D1D] via-[#f05a28] to-[#FFAB8F] px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/55 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141416]"
            onClick={onClose}
          >
            {t("upgradeModalCta")}
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}
